import { Component } from '@angular/core';
import { OnInit, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { AlertsService } from '../../../services/alerts.service';
import { UserService } from '../../../services/user.service';
import { TaskService } from '../../services/task.service';
import { TaskManagementComponent } from '../../components/task-management/task-management.component';
import { TaskCompletionComponent } from '../../components/task-completion/task-completion.component';
import { TaskPropertiesComponent } from  '../../components/task-properties/task-properties.component';
import { LogsComponent } from  '../../components/logs/logs.component';
import { formatString } from '../../../utils/string-utils';

/**
 * Main workflow unclaimed tasks management component.
 */
@Component({
	selector: 'workflow-unclaimed-tasks',
	templateUrl: './unclaimed-tasks.component.html',
	styleUrl: './unclaimed-tasks.component.css'
})
export class UnclaimedTasksComponent implements OnInit, OnDestroy {

	private taskEventSubscription!: Subscription;
	private userServiceSubscription!: Subscription;

	LOCALIZATION: any;
	userPermissions: { [key: string]: any } = {};

	inprogress: boolean = false;

	tasks: any[] = [];
	selectedTasks = {
		all: false,
		selected: 0,
		canClaim: false,
		canAssign: false,
		canComplete: false,
		canReject: false,
		canRelease: false
	};

	filters = [
		{ name: '', label: '' },
		{ name: 'productTitle', label: 'Document' },
		{ name: 'taskName', label: 'Task' },
		{ name: 'batchName', label: 'Batch' },
		{ name: 'workflowType', label: 'Workflow' }
	];
	filter = {
		active: this.filters[0],
		value: ''
	};
	filterOpenState: boolean = true;

	constraints: any = {};
	pagination = {
		page: 1,
		elementsPerPage: 10,
		elementsPerPageSizes: [10, 20, 50],
		sorting: {
			sort: 'dateDue,desc',
		},
		totalElements: 0,
		pageGap: 1,
		resultTotal: 0,
		pageChanged: (newPage: any) => {
			this.pagination.page = newPage;
			this.loadUnassignedTasks(this.pagination.page);
		}
	};
	private isSortDirAsc: boolean = true;

	constructor(
		private ngbModal: NgbModal,
		private userService: UserService,
		private alertsService: AlertsService,
		private taskService: TaskService
	) {
		this.LOCALIZATION = this.userService.LOCALIZATION;
		this.userPermissions = this.userService.getUserPermissionsMap();
	}

	ngOnInit(): void {
		this.taskEventSubscription = this.taskService.workflowTaskEvent.subscribe(event => {
			console.log('Event received:', event);
			this.loadUnassignedTasks();
		});
		
		this.userServiceSubscription = this.userService.broadcastEvent.subscribe((eventId: string) => {
			if ("localizationLoaded" === eventId) {
				this.LOCALIZATION = this.userService.LOCALIZATION;
			} else if ("userPrefsUpdate" === eventId) {
				this.userPermissions = this.userService.getUserPermissionsMap();
			}
		});			

		this.loadUnassignedTasks();
	}

	ngOnDestroy() {
		if (this.taskEventSubscription) {
			this.taskEventSubscription.unsubscribe();
		}
		if (this.userServiceSubscription) {
			this.userServiceSubscription.unsubscribe();
		}		
	}

	/**
	 * Loads the tasks using the current constraints.
	 */
	loadUnassignedTasks(page?: number): void {
		this.inprogress = true;

		if (page) {
			this.pagination.page = page;
		}

		let index = this.pagination.page - 1;

		this.taskService.getPotentialTasksForUser(this.userService.user.username as string, index, this.pagination, this.constraints).then(
			(data: any) => {
				if (!data) {
					this.tasks = [];
				} else {
					this.tasks = data.content;
					this.pagination.page = data.page.number + 1;
					this.pagination.elementsPerPage = data.page.size;
					this.pagination.totalElements = data.page.totalElements;
					this.pagination.pageGap = 1 + (data.page.size * data.page.number);
					this.pagination.resultTotal = this.tasks.length + (data.page.size * data.page.number);
				}
				this.inprogress = false;
				this.updateTaskStatus();
			},
			(err: any) => {
				console.error("Failed to fetch tasks", err);
				this.tasks = [];
				this.inprogress = false;
				this.updateTaskStatus();
			}
		);
	}

	clearFilter(): void {
		this.constraints = {};
		this.filter.active = this.filters[0];
		this.filter.value = '';
		this.applyFilter();
	}

	applyFilter(event?: Event): void {
		this.constraints = {};
		if (this.filter.active.name && this.filter.value) {
			this.constraints[this.filter.active.name] = this.filter.value;

			// Deprecated: OCSC
			//			if (this.filter.active.isMetadata) {
			//				constraints = { "productMetadata": {} };
			//				constraints.productMetadata[this.filter.active.field] = this.filter.value;
			//			}			
		}

		this.loadUnassignedTasks();
	}

	pageSizeChanged(size: number): void {
		this.pagination.elementsPerPage = size;
		this.loadUnassignedTasks(1);
	}

	sortingChanged(sortby: string): void {
		this.isSortDirAsc = !this.isSortDirAsc;
		let direction = this.isSortDirAsc ? 'asc' : 'desc';
		this.pagination.sorting = { sort: sortby + ',' + direction };
		this.loadUnassignedTasks(1);
	}

	private updateTaskStatus(args?: any): void {
		this.selectedTasks = {
			all: false,
			selected: 0,
			canClaim: false,
			canAssign: false,
			canComplete: false,
			canReject: false,
			canRelease: false
		};

		let allCanClaim = true;
		let allCanAssign = true;
		let allCanComplete = true;
		let allCanReject = true;
		let allCanRelease = true;

		this.tasks.forEach((task: any) => {
			if (args && args.hasOwnProperty('allToggled')) task.selected = args.allToggled;

			if (task.selected) {
				this.selectedTasks.selected = this.selectedTasks.selected + 1;

				if (!task.actions.canClaim) allCanClaim = false;
				if (!task.actions.canAssign) allCanAssign = false;
				if (!task.actions.canComplete) allCanComplete = false;
				if (!task.actions.canReject) allCanReject = false;
				if (!task.actions.canRelease) allCanRelease = false;
			}
		});

		if (this.selectedTasks.selected > 0) {
			this.selectedTasks.canClaim = allCanClaim;
			this.selectedTasks.canAssign = allCanAssign;
			this.selectedTasks.canComplete = allCanComplete;
			this.selectedTasks.canReject = allCanReject;
			this.selectedTasks.canRelease = allCanRelease;
		}
	}

	toggleAllTasks(): void {
		let toggleStatus = !this.selectedTasks.all;
		this.tasks.forEach((task: any) => {
			task.selected = toggleStatus;
		});
		this.updateTaskStatus({ allToggled: toggleStatus });
	}

	toggleTaskId(id: number): void {
		this.selectedTasks.all = this.tasks.every((t: any) => t.selected);
		this.updateTaskStatus();
	}


	/**
	 * Claims the chosen tasks, assigning them to the user.
	 */
	onClaim(id?: number): void {
		let payload = {
			taskIds: [] as number[],
			comment: null,
			ownerId: this.userService.user.username
		};

		if (id) {
			payload.taskIds = [id];
		} else {
			for (let i = 0, len = this.tasks.length; i < len; i++) {
				let t = this.tasks[i];
				if (t.selected) { payload.taskIds.push(t.taskId); }
			}
		}

		this.taskService.massClaim(payload).then(
			(response: any) => {
				this.alertsService.success({
					message: formatString(this.LOCALIZATION.claimTasks, this.LOCALIZATION.mytasks),
					delay: 5000
				});
			},
			(err: any) => {
				console.error("Failed to claim tasks", err);
				this.alertsService.error({
					message: "Failed to claim the selected tasks.",
					delay: 5000
				});
			}
		);
	}

	/**
	 * Assigns the chosen tasks to a user.
	 */
	onAssign(id?: number): void {
		const modalRef = this.ngbModal.open(TaskManagementComponent);

		let taskids: number[] = [];
		if (id) {
			taskids = [id];
		} else {
			for (let i = 0, len = this.tasks.length; i < len; i++) {
				let t = this.tasks[i];
				if (t.selected) { taskids.push(t.taskId); }
			}
		}

		modalRef.componentInstance.taskGroup = {
			taskids: taskids,
			mode: 'assign',
			comment: null,
			owner: null
		};

		modalRef.result.then(
			(payload) => {
				console.log('Modal dismissed');
			},
			(reason) => {
				console.log('Modal dismissed');
			}
		);
	}

	/**
	 * Completes the chosen tasks.
	 */
	onComplete(id?: number): void {
		const modalRef = this.ngbModal.open(TaskCompletionComponent);

		let taskids: number[] = [];
		if (id) {
			taskids = [id];
		} else {
			for (let i = 0, len = this.tasks.length; i < len; i++) {
				let t = this.tasks[i];
				if (t.selected) { taskids.push(t.taskId); }
			}
		}

		modalRef.componentInstance.taskGroup = {
			taskids: taskids,
			comment: null,
			owner: this.userService.user.username,
			annotations: [],
			canSave: false
		};

		modalRef.result.then(
			(payload) => {
				console.log('Modal dismissed');
			},
			(reason) => {
				console.log('Modal dismissed');
			}
		);
	}

	/**
	 * Rejects the chosen tasks.
	 */
	onReject(id?: number): void {
		const modalRef = this.ngbModal.open(TaskManagementComponent);

		let taskids: number[] = [];
		if (id) {
			taskids = [id];
		} else {
			for (let i = 0, len = this.tasks.length; i < len; i++) {
				let t = this.tasks[i];
				if (t.selected) { taskids.push(t.taskId); }
			}
		}

		modalRef.componentInstance.taskGroup = {
			taskids: taskids,
			mode: 'reject',
			comment: null,
			owner: this.userService.user.username
		};

		modalRef.result.then(
			(payload) => {
				console.log('Modal dismissed');
			},
			(reason) => {
				console.log('Modal dismissed');
			}
		);
	}

	/**
	 * Displays logs for the task.
	 */
	showTaskLogs(task: any): void { 
		const modalRef = this.ngbModal.open(LogsComponent);

		modalRef.componentInstance.args = {
			title: "Task Logs",
			subtitle: task.taskName + " - " + task.batchName + " - " + task.productTitle,
			id: task.taskId,
			type: "task"
		};

		modalRef.result.then(
			(payload) => {
				console.log('Modal dismissed');
			},
			(reason) => {
				console.log('Modal dismissed');
			}
		);		
	}

	/**
	 * Displays task properties.
	 */
	showTaskProperties(task: any): void {
		const modalRef = this.ngbModal.open(TaskPropertiesComponent);

		modalRef.componentInstance.task = task;

		modalRef.result.then(
			(payload) => {
				console.log('Modal dismissed');
			},
			(reason) => {
				console.log('Modal dismissed');
			}
		);		
	 }

	// Deprecated: OCSC
	//		this.onClaimForBatch = function (batchUUID, batchName, taskId) {
	//			var payload = {
	//				comment: null,
	//				ownerId: this.user.username
	//			}
	//
	//			dxTaskService.massClaimForBatch(batchUUID, taskId, payload).then(function (result) {
	//            	alertsService.success({
	//            		message: this.LOCALIZATION.claimBatchTasks.format(batchName, this.LOCALIZATION.mytasks),
	//            		delay: 5000
	//            	});
	//			}, function () {
	//            	alertsService.error({
	//            		message: "Failed to claim the tasks.",
	//            		delay: 5000
	//            	});
	//			});
	//		}	
}
